import { useState, useEffect, useCallback, useMemo } from 'react'
import type { TableInfo, ColumnInfo, ForeignKeyInfo, FkEntryRequest } from '../api/types'
import type { ApiClient } from '../api/client'
import { NotesEditor } from './NotesEditor'
// Note: ApiError import removed - FK creation now handled transactionally by parent component
import {
  PROCEDURE_TYPE_CATEGORIES,
  getProcedureTypeCategory,
  getFieldsForProcedureType,
  getProcedureTypeDescription,
  getViewDefaultProcedureType,
  type ProcedureTypeCategory,
} from '../config/procedureTypeFields'

interface LookupOption {
  id: string
  label: string
}

// Columns that should show dropdown suggestions from existing values (no FK reference)
const SUGGESTION_COLUMNS = ['sample_type', 'sample_subtype', 'procedure_type', 'status_sample', 'status_patient', 'priority', 'performed_on']

// Tables that use procedure type-based field filtering
const PROCEDURE_TYPE_TABLES = ['samples', 'vw_master_events_timeline']

// Mapping from view display column names to database ID column names
// Views return joined display names, but the form needs the FK IDs
// IMPORTANT: Keep this mapping complete - unmapped columns will cause FK resolution failures
const VIEW_TO_DB_COLUMN_MAPPING: Record<string, string> = {
  'subject': 'subject_id',
  'banking_entity': 'banking_entity_id',
  'performing_entity': 'performing_entity_id',
  'location_performed': 'location_performed_id',
  'location_destination': 'location_destination_id',
  'transfer_origin': 'transfer_origin_id',
  'transfer_destination': 'transfer_destination_id',
  'source_sample_label': 'source_sample_id',
  'blocked_by': 'blocked_by_sample_id', // Added: maps blocked_by display column to the new FK column
}

interface CreateEditFormProps {
  tableInfo: TableInfo
  existingData?: Record<string, unknown>
  onSubmit: (data: Record<string, unknown>, fkEntries?: FkEntryRequest[]) => void
  onCancel: () => void
  isLoading?: boolean
  mode: 'create' | 'edit'
  prefillDate?: Date
  apiClient?: ApiClient
  viewName?: string // Pass the current view name to auto-select procedure type
}

export function CreateEditForm({
  tableInfo,
  existingData,
  onSubmit,
  onCancel,
  isLoading = false,
  mode,
  prefillDate,
  apiClient,
  viewName,
}: CreateEditFormProps) {
  const [formData, setFormData] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [lookupData, setLookupData] = useState<Record<string, LookupOption[]>>({})
  const [lookupLoading, setLookupLoading] = useState(false)
  const [suggestionData, setSuggestionData] = useState<Record<string, string[]>>({})
  const [addNewMode, setAddNewMode] = useState<Record<string, boolean>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [fkResolutionDone, setFkResolutionDone] = useState(false)

  // Procedure type selection state (for samples/timeline tables)
  const [selectedProcedureType, setSelectedProcedureType] = useState<ProcedureTypeCategory | null>(null)
  const [showProcedureTypeSelector, setShowProcedureTypeSelector] = useState(false)

  // Check if this table uses procedure type filtering
  const usesProcedureTypeFiltering = PROCEDURE_TYPE_TABLES.some(
    t => tableInfo.tableName.includes(t) || tableInfo.tableName === t
  )

  // Determine the effective procedure type (from selection or existing data)
  const effectiveProcedureType = useMemo((): ProcedureTypeCategory | null => {
    if (selectedProcedureType) return selectedProcedureType
    if (existingData?.procedure_type) {
      return getProcedureTypeCategory(existingData.procedure_type as string)
    }
    return null
  }, [selectedProcedureType, existingData?.procedure_type])

  // Get allowed fields for the current procedure type
  const allowedFields = useMemo((): string[] | null => {
    if (!usesProcedureTypeFiltering) return null
    if (!effectiveProcedureType) return null
    return getFieldsForProcedureType(effectiveProcedureType)
  }, [usesProcedureTypeFiltering, effectiveProcedureType])

  // Determine if form data is ready for editing
  // In edit mode, we need to wait for FK lookup data and resolution to complete
  const dataReady = useMemo((): boolean => {
    if (mode === 'create') return true
    // In edit mode, wait for lookup loading to complete and FK resolution to be done
    const hasFkColumns = tableInfo.foreignKeys && tableInfo.foreignKeys.length > 0
    if (hasFkColumns) {
      return !lookupLoading && fkResolutionDone
    }
    // No FK columns - just need form data to be initialized (always happens synchronously)
    return true
  }, [mode, lookupLoading, fkResolutionDone, tableInfo.foreignKeys])

  // Initialize procedure type selector state for create mode
  // If creating from a view with a default procedure type, auto-select it
  useEffect(() => {
    if (mode === 'create' && usesProcedureTypeFiltering && !selectedProcedureType) {
      const viewDefaultType = getViewDefaultProcedureType(viewName)
      if (viewDefaultType) {
        // Auto-select the procedure type based on the view
        setSelectedProcedureType(viewDefaultType)
        setShowProcedureTypeSelector(false)
        // Pre-fill the procedure_type field
        const defaultValues: Record<ProcedureTypeCategory, string> = {
          'Sampling - patient': 'Sampling - patient',
          'EEG monitoring': 'EEG monitoring',
          'Transfer / shipping': 'Transfer / shipping',
          'Processing / maturation': 'Processing / maturation',
          'Other': 'Other',
        }
        setFormData(prev => ({ ...prev, procedure_type: defaultValues[viewDefaultType] }))
      } else {
        setShowProcedureTypeSelector(true)
      }
    }
  }, [mode, usesProcedureTypeFiltering, selectedProcedureType, viewName])

  // Handle escape key to close modal
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel()
    }
  }, [onCancel])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  // Fetch lookup data for foreign key columns
  useEffect(() => {
    if (!apiClient || !tableInfo.foreignKeys?.length) return

    const fetchLookups = async () => {
      setLookupLoading(true)
      const newLookupData: Record<string, LookupOption[]> = {}

      // Get unique referenced tables to avoid duplicate fetches
      const tableMap = new Map<string, ForeignKeyInfo[]>()
      for (const fk of tableInfo.foreignKeys) {
        const existing = tableMap.get(fk.referencedTable) || []
        existing.push(fk)
        tableMap.set(fk.referencedTable, existing)
      }

      for (const [referencedTable, fks] of tableMap) {
        try {
          // Increased limit from 200 to 1000 to handle larger reference tables
          const data = await apiClient.getRows(referencedTable, 1, 1000)
          // Determine the display column based on the table
          let displayCol: string
          if (referencedTable === 'subjects') {
            displayCol = 'display_name'
          } else if (referencedTable === 'samples') {
            displayCol = 'title'
          } else {
            displayCol = 'name'
          }
          const options = data.rows
            .map((row) => ({
              id: String(row[fks[0].referencedColumn] || ''),
              label: String(row[displayCol] || row[fks[0].referencedColumn] || ''),
            }))
            .sort((a, b) => a.label.localeCompare(b.label)) // Sort alphabetically by label

          // Assign the same options to all FK columns that reference this table
          for (const fk of fks) {
            newLookupData[fk.columnName] = options
          }
        } catch (err) {
          console.error(`Failed to fetch lookup data for ${referencedTable}:`, err)
        }
      }

      setLookupData(newLookupData)
      setLookupLoading(false)
    }

    fetchLookups()
  }, [apiClient, tableInfo.foreignKeys])

  // Fetch distinct values for suggestion columns (sample_type, sample_subtype, etc.)
  // Uses the dedicated distinct values endpoint for better performance and complete data
  useEffect(() => {
    if (!apiClient) return

    // Only fetch for the samples table (or related tables with these columns)
    const suggestionCols = tableInfo.columns
      .filter(col => SUGGESTION_COLUMNS.includes(col.columnName))
      .map(col => col.columnName)

    if (suggestionCols.length === 0) return

    const fetchSuggestions = async () => {
      try {
        const newSuggestions: Record<string, string[]> = {}

        // Fetch distinct values for each suggestion column using the dedicated endpoint
        await Promise.all(
          suggestionCols.map(async (col) => {
            try {
              const response = await apiClient.getDistinctColumnValues(tableInfo.tableName, col)
              newSuggestions[col] = response.values
            } catch (err) {
              console.error(`Failed to fetch distinct values for ${col}:`, err)
              newSuggestions[col] = []
            }
          })
        )

        setSuggestionData(newSuggestions)
      } catch (err) {
        console.error('Failed to fetch suggestion data:', err)
      }
    }

    fetchSuggestions()
  }, [apiClient, tableInfo.tableName, tableInfo.columns])

  // Initialize form data from existingData
  useEffect(() => {
    const initial: Record<string, string> = {}
    tableInfo.columns.forEach((col) => {
      if (existingData && existingData[col.columnName] !== undefined) {
        const value = existingData[col.columnName]
        if (value === null) {
          initial[col.columnName] = ''
        } else if (col.dataType.toLowerCase() === 'date' && value) {
          // Format date values as yyyy-MM-dd for HTML date inputs
          const dateStr = String(value)
          // Handle ISO timestamps by taking just the date part
          if (dateStr.includes('T')) {
            initial[col.columnName] = dateStr.split('T')[0]
          } else {
            initial[col.columnName] = dateStr
          }
        } else if (typeof value === 'object') {
          // Properly serialize objects/arrays (e.g., outcome_notes) as JSON
          initial[col.columnName] = JSON.stringify(value, null, 2)
        } else {
          initial[col.columnName] = String(value)
        }
      } else if (prefillDate && col.columnName === 'event_start_date') {
        // Prefill the event start date if provided
        initial[col.columnName] = prefillDate.toISOString().split('T')[0]
      } else {
        initial[col.columnName] = ''
      }
    })
    setFormData(initial)
  }, [tableInfo, existingData, prefillDate])

  // Resolve FK IDs from view display names after lookup data is loaded
  // Views return display columns (e.g., 'subject') but form needs FK IDs (e.g., 'subject_id')
  useEffect(() => {
    // In edit mode, mark FK resolution as done once lookup data is loaded
    // (or immediately if there are no FK columns to resolve)
    if (mode === 'edit') {
      const hasFkColumns = tableInfo.foreignKeys && tableInfo.foreignKeys.length > 0
      if (!hasFkColumns) {
        setFkResolutionDone(true)
        return
      }
      if (!lookupData || Object.keys(lookupData).length === 0) return
    }

    if (!existingData || !lookupData || Object.keys(lookupData).length === 0) {
      return
    }

    setFormData(prev => {
      const updates: Record<string, string> = {}

      // For each ID column that's empty but has a corresponding display column in existingData
      for (const [viewCol, dbCol] of Object.entries(VIEW_TO_DB_COLUMN_MAPPING)) {
        const displayValue = existingData[viewCol]
        const currentIdValue = prev[dbCol]

        // If we have a display value but no ID value, try to look up the ID
        if (displayValue && !currentIdValue && lookupData[dbCol]) {
          const displayStr = String(displayValue).toLowerCase().trim()
          // Find all matching options (case-insensitive)
          const matchingOptions = lookupData[dbCol].filter(
            opt => opt.label.toLowerCase().trim() === displayStr
          )

          if (matchingOptions.length > 1) {
            // Warn about duplicate display names - first match will be used
            console.warn(
              `[FK Resolution] Multiple matches found for "${displayValue}" in ${dbCol}:`,
              matchingOptions.map(o => ({ id: o.id, label: o.label }))
            )
          }

          if (matchingOptions.length > 0) {
            updates[dbCol] = matchingOptions[0].id
          } else {
            // No match found - log for debugging
            console.warn(
              `[FK Resolution] No match found for "${displayValue}" in ${dbCol}. Available options:`,
              lookupData[dbCol].length > 10
                ? `${lookupData[dbCol].length} options (too many to list)`
                : lookupData[dbCol].map(o => o.label)
            )
          }
        }
      }

      if (Object.keys(updates).length > 0) {
        return { ...prev, ...updates }
      }
      return prev
    })

    // Mark FK resolution as done after processing
    if (mode === 'edit') {
      setFkResolutionDone(true)
    }
  }, [existingData, lookupData, mode, tableInfo.foreignKeys])

  // Columns that can be edited (exclude auto-generated PKs and timestamps)
  const editableColumns = useMemo(() => {
    return tableInfo.columns.filter((col) => {
      // In edit mode, don't allow editing the primary key
      if (mode === 'edit' && col.isPrimaryKey) return false

      // Skip auto-generated columns
      if (col.columnDefault?.includes('gen_random_uuid()')) return false
      if (col.columnDefault?.includes('now()')) return false
      if (col.columnName === 'created_at') return false
      if (col.columnName === 'updated_at') return false

      // Filter by procedure type if applicable
      if (allowedFields !== null) {
        // Always show procedure_type field
        if (col.columnName === 'procedure_type') return true
        // Only show fields allowed for this procedure type
        if (!allowedFields.includes(col.columnName)) return false
      }

      return true
    })
  }, [tableInfo.columns, mode, allowedFields])

  const handleChange = (columnName: string, value: string) => {
    setFormData((prev) => ({ ...prev, [columnName]: value }))
    // Clear error when user types
    if (errors[columnName]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[columnName]
        return next
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate required fields
    const newErrors: Record<string, string> = {}
    editableColumns.forEach((col) => {
      const value = formData[col.columnName]?.trim()

      // Required field validation
      if (!col.isNullable && !value && !col.columnDefault) {
        newErrors[col.columnName] = 'This field is required'
        return
      }

      // JSON validation - validate JSON fields before submission
      const dataType = col.dataType.toLowerCase()
      if ((dataType === 'json' || dataType === 'jsonb') && value) {
        try {
          JSON.parse(value)
        } catch (err) {
          newErrors[col.columnName] = 'Invalid JSON format. Please check the syntax.'
        }
      }
    })

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsSubmitting(true)

    // Build data object, converting types as needed
    const data: Record<string, unknown> = {}

    // Collect FK entries that need to be created in "add new" mode
    // These will be created transactionally with the main row by the parent component
    const fkEntriesToCreate: FkEntryRequest[] = []

    for (const col of editableColumns) {
      const value = formData[col.columnName]?.trim()
      if (!value) continue

      const fkInfo = getFkInfo(col.columnName)
      if (fkInfo && addNewMode[col.columnName]) {
        // This is a FK column in "add new" mode - collect for transactional creation
        const displayCol = fkInfo.referencedTable === 'subjects' ? 'display_name' : 'name'
        fkEntriesToCreate.push({
          referencedTable: fkInfo.referencedTable,
          referencedColumn: fkInfo.referencedColumn,
          targetColumn: col.columnName,
          data: { [displayCol]: value },
        })
        // Don't add to data - the transactional API will set the FK ID after creation
      } else {
        data[col.columnName] = convertValue(value, col)
      }
    }

    // Handle nullable empty values
    editableColumns.forEach((col) => {
      const value = formData[col.columnName]?.trim()
      // Skip columns that are being created via FK entries
      if (fkEntriesToCreate.some(fk => fk.targetColumn === col.columnName)) return

      if (!data[col.columnName] && !col.isNullable && col.columnDefault) {
        // Let the database use the default
      } else if (value === '' && col.isNullable && !data[col.columnName]) {
        data[col.columnName] = null
      }
    })

    setIsSubmitting(false)
    onSubmit(data, fkEntriesToCreate.length > 0 ? fkEntriesToCreate : undefined)
  }

  const convertValue = (value: string, col: ColumnInfo): unknown => {
    switch (col.dataType.toLowerCase()) {
      case 'boolean':
      case 'bool':
        return value.toLowerCase() === 'true' || value === '1'
      case 'integer':
      case 'int':
      case 'int4':
        return parseInt(value, 10)
      case 'bigint':
      case 'int8':
        // Keep bigint as string to avoid JavaScript precision loss (Number.MAX_SAFE_INTEGER = 9007199254740991)
        // Backend handles string-to-bigint conversion safely
        return value
      case 'jsonb':
      case 'json':
        // JSON validation happens in handleSubmit before this is called
        // If we reach here with invalid JSON, it's a bug - but parse anyway for safety
        return JSON.parse(value)
      default:
        return value
    }
  }

  const getInputType = (col: ColumnInfo): string => {
    switch (col.dataType.toLowerCase()) {
      case 'boolean':
      case 'bool':
        return 'checkbox'
      case 'integer':
      case 'int':
      case 'int4':
      case 'bigint':
      case 'int8':
        return 'number'
      case 'date':
        return 'date'
      default:
        return 'text'
    }
  }

  // Check if a column has FK lookup data available
  const getFkInfo = (columnName: string): ForeignKeyInfo | undefined => {
    return tableInfo.foreignKeys?.find(fk => fk.columnName === columnName)
  }

  // Check if a column has suggestion data (and is not in "add new" mode)
  const hasSuggestions = (columnName: string): boolean => {
    return SUGGESTION_COLUMNS.includes(columnName) &&
           (suggestionData[columnName]?.length ?? 0) > 0 &&
           !addNewMode[columnName]
  }

  // Format column name for display (e.g., "subject_id" -> "Subject")
  const formatColumnLabel = (columnName: string): string => {
    return columnName
      .replace(/_id$/, '')
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  // Handle switching to "add new" mode for suggestion columns
  const handleAddNew = (columnName: string) => {
    setAddNewMode(prev => ({ ...prev, [columnName]: true }))
    handleChange(columnName, '') // Clear the value to let user type
  }

  // Handle going back to dropdown mode
  const handleBackToDropdown = (columnName: string) => {
    setAddNewMode(prev => ({ ...prev, [columnName]: false }))
  }

  // Handle procedure type selection
  const handleProcedureTypeSelect = (procedureType: ProcedureTypeCategory) => {
    setSelectedProcedureType(procedureType)
    setShowProcedureTypeSelector(false)
    // Pre-fill the procedure_type field with a default value based on category
    const defaultValues: Record<ProcedureTypeCategory, string> = {
      'Sampling - patient': 'Sampling - patient',
      'EEG monitoring': 'EEG monitoring',
      'Transfer / shipping': 'Transfer / shipping',
      'Processing / maturation': 'Processing / maturation',
      'Other': 'Other',
    }
    setFormData(prev => ({ ...prev, procedure_type: defaultValues[procedureType] }))
  }

  // Render procedure type selector for create mode
  if (showProcedureTypeSelector && usesProcedureTypeFiltering) {
    return (
      <div className="modal-overlay" onClick={onCancel}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3 style={{ margin: 0 }}>Select Event Type</h3>
            <p style={{ margin: 0, color: 'var(--color-text-secondary)', marginTop: 'var(--spacing-xs)' }}>
              Choose the type of event to create. This determines which fields will be available.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)', padding: 'var(--spacing-md)' }}>
            {PROCEDURE_TYPE_CATEGORIES.map((procedureType) => (
              <button
                key={procedureType}
                type="button"
                className="btn btn-secondary"
                onClick={() => handleProcedureTypeSelect(procedureType)}
                style={{
                  padding: 'var(--spacing-md)',
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--spacing-xs)',
                }}
              >
                <span style={{ fontWeight: 600 }}>{procedureType}</span>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                  {getProcedureTypeDescription(procedureType)}
                </span>
              </button>
            ))}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
        {/* Loading overlay for edit mode while data is being resolved */}
        {!dataReady && (
          <div className="modal-loading-overlay">
            <div className="spinner" />
            <span style={{ color: 'var(--color-text-muted)' }}>Loading data...</span>
          </div>
        )}
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>
            {mode === 'create' ? 'Create New Row' : 'Edit Row'}
          </h3>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)', marginTop: 'var(--spacing-xs)' }}>
            {effectiveProcedureType ? (
              <>Event Type: <span className="mono">{effectiveProcedureType}</span></>
            ) : (
              <>Table: <span className="mono">{tableInfo.tableName}</span></>
            )}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ maxHeight: '60vh', overflow: 'auto', padding: 'var(--spacing-xs)' }}>
            {editableColumns.map((col) => (
              <div key={col.columnName} style={{ marginBottom: 'var(--spacing-md)' }}>
                <label htmlFor={col.columnName}>
                  {formatColumnLabel(col.columnName)}
                  {!col.isNullable && !col.columnDefault && (
                    <span style={{ color: 'var(--color-danger)' }}> *</span>
                  )}
                </label>

                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                  {/* FK columns with lookup data get dropdown selectors with add new option */}
                  {lookupData[col.columnName] && !addNewMode[col.columnName] ? (
                    <select
                      id={col.columnName}
                      value={formData[col.columnName] || ''}
                      onChange={(e) => {
                        if (e.target.value === '__ADD_NEW__') {
                          handleAddNew(col.columnName)
                        } else {
                          handleChange(col.columnName, e.target.value)
                        }
                      }}
                      style={{
                        borderColor: errors[col.columnName] ? 'var(--color-danger)' : undefined,
                      }}
                      disabled={lookupLoading}
                    >
                      <option value="">
                        {lookupLoading ? 'Loading...' : `Select ${formatColumnLabel(col.columnName)}`}
                      </option>
                      {lookupData[col.columnName].map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.label}
                        </option>
                      ))}
                      <option value="__ADD_NEW__">+ Add new...</option>
                    </select>
                  ) : hasSuggestions(col.columnName) ? (
                    /* Suggestion columns (sample_type, etc.) with dropdown + add new option */
                    <>
                      <select
                        id={col.columnName}
                        value={formData[col.columnName] || ''}
                        onChange={(e) => {
                          if (e.target.value === '__ADD_NEW__') {
                            handleAddNew(col.columnName)
                          } else {
                            handleChange(col.columnName, e.target.value)
                          }
                        }}
                        style={{
                          borderColor: errors[col.columnName] ? 'var(--color-danger)' : undefined,
                        }}
                      >
                        <option value="">Select {formatColumnLabel(col.columnName)}</option>
                        {suggestionData[col.columnName]?.map((val) => (
                          <option key={val} value={val}>
                            {val}
                          </option>
                        ))}
                        <option value="__ADD_NEW__">+ Add new...</option>
                      </select>
                    </>
                  ) : addNewMode[col.columnName] ? (
                    /* "Add new" mode - show Cancel button only for FK columns (creates entry in another table) */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)', flex: 1 }}>
                      <div style={{ display: 'flex', gap: 'var(--spacing-xs)' }}>
                        <input
                          type="text"
                          id={col.columnName}
                          value={formData[col.columnName] || ''}
                          onChange={(e) => handleChange(col.columnName, e.target.value)}
                          placeholder={`Enter new ${formatColumnLabel(col.columnName).toLowerCase()}`}
                          style={{
                            flex: 1,
                            borderColor: errors[col.columnName] ? 'var(--color-danger)' : undefined,
                          }}
                          autoFocus
                        />
                        {/* Only show Cancel for FK columns - adding new creates entry in referenced table */}
                        {getFkInfo(col.columnName) && (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => handleBackToDropdown(col.columnName)}
                            style={{ padding: '4px 8px', fontSize: 'var(--font-size-xs)' }}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                      {getFkInfo(col.columnName) && (
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-warning)' }}>
                          This will create a new entry in {getFkInfo(col.columnName)!.referencedTable}
                        </span>
                      )}
                    </div>
                  ) : col.dataType.toLowerCase() === 'boolean' ? (
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-sm)',
                      cursor: 'pointer',
                    }}>
                      <input
                        type="checkbox"
                        id={col.columnName}
                        checked={formData[col.columnName] === 'true'}
                        onChange={(e) => handleChange(col.columnName, String(e.target.checked))}
                        style={{ width: 'auto' }}
                      />
                      <span style={{ fontWeight: 'normal' }}>
                        {formData[col.columnName] === 'true' ? 'true' : 'false'}
                      </span>
                    </label>
                  ) : col.columnName === 'outcome_notes' ? (
                    <NotesEditor
                      value={formData[col.columnName] || ''}
                      onChange={(value) => handleChange(col.columnName, value)}
                      error={errors[col.columnName]}
                    />
                  ) : col.dataType.toLowerCase() === 'text' ? (
                    <textarea
                      id={col.columnName}
                      value={formData[col.columnName] || ''}
                      onChange={(e) => handleChange(col.columnName, e.target.value)}
                      rows={2}
                      style={{
                        borderColor: errors[col.columnName] ? 'var(--color-danger)' : undefined,
                      }}
                    />
                  ) : col.dataType.toLowerCase() === 'jsonb' || col.dataType.toLowerCase() === 'json' ? (
                    <textarea
                      id={col.columnName}
                      value={formData[col.columnName] || ''}
                      onChange={(e) => handleChange(col.columnName, e.target.value)}
                      rows={3}
                      placeholder='{"key": "value"}'
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        borderColor: errors[col.columnName] ? 'var(--color-danger)' : undefined,
                      }}
                    />
                  ) : (
                    <input
                      type={getInputType(col)}
                      id={col.columnName}
                      value={formData[col.columnName] || ''}
                      onChange={(e) => handleChange(col.columnName, e.target.value)}
                      style={{
                        borderColor: errors[col.columnName] ? 'var(--color-danger)' : undefined,
                      }}
                    />
                  )}
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: 'var(--spacing-xs)',
                }}>
                  <span style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                  }}>
                    {getFkInfo(col.columnName) ? (
                      <>references {getFkInfo(col.columnName)!.referencedTable}</>
                    ) : (
                      col.dataType
                    )}
                    {col.isNullable && ' (nullable)'}
                  </span>
                  {errors[col.columnName] && (
                    <span style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-danger)',
                    }}>
                      {errors[col.columnName]}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="modal-footer" style={{ flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
            {Object.keys(errors).length > 0 && (
              <div style={{
                width: '100%',
                padding: 'var(--spacing-sm) var(--spacing-md)',
                backgroundColor: 'var(--color-danger-bg, rgba(220, 38, 38, 0.1))',
                border: '1px solid var(--color-danger)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-danger)',
                fontSize: 'var(--font-size-sm)',
              }}>
                Please fill in all required fields: {Object.keys(errors).map(formatColumnLabel).join(', ')}
              </div>
            )}
            <div style={{ display: 'flex', gap: 'var(--spacing-sm)', justifyContent: 'flex-end', width: '100%' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
                disabled={isLoading || isSubmitting || !dataReady}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading || isSubmitting || !dataReady}
              >
                {!dataReady ? 'Loading...' : isLoading || isSubmitting ? 'Saving...' : mode === 'create' ? 'Create' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
